import { Component, OnInit, inject } from '@angular/core'
import { TableLazyLoadEvent } from 'primeng/table'
import { MessageService } from 'primeng/api'

import { REPORT_CATALOG, REPORT_CATALOG_BY_TYPE, ReportCatalogEntry } from '../data/constants/report-catalog.constant'
import { ReportHistoryRow, ReportSummaryRow, ReportType } from '../data/interfaces/reporteria.interface'
import { ApiReportsService } from '../data/services/api/api-reports.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { guardarBlobComo } from '@data/utils/descarga-archivo.util'

/** Una tarjeta del catálogo, ya cruzada con su pulso. */
interface TarjetaReporte extends ReportCatalogEntry {
  total: number
  last: string | null
  lastBy: string | null
  /** Si quien mira puede entrar a la pantalla donde se genera. */
  alcanzable: boolean
}

/**
 * Reportería: el catálogo de los reportes que existen y el rastro de quién bajó cuál.
 *
 * ## Qué es y qué no
 *
 * **No genera nada.** Cada reporte se saca desde la pantalla donde viven sus datos, porque ahí están
 * sus filtros: la cartera sale del mes que estás mirando, la mora de la fecha de corte que elegiste
 * y la hoja de carga del archivo que acabas de subir. Aquí se contesta lo otro: *qué reportes hay*,
 * *dónde se sacan*, y *quién se llevó cuál* — con el archivo, exactamente el que se entregó.
 *
 * ## Por qué el archivo y no una regeneración
 *
 * Volver a generar con los mismos parámetros daría **otro archivo**: la cartera de un mes abierto se
 * mueve con cada pago aplicado y la mora se recalcula cada noche. Quien abre el historial para saber
 * qué se entregó recibiría algo distinto bajo la misma etiqueta y sin nada que lo advirtiera. Por eso
 * se guarda el archivo — ver `ReportArchiveService` en la API.
 *
 *! Los reportes generados **antes** de este cambio no tienen archivo guardado. Su fila existe y su
 *! botón de descarga no: es la diferencia entre «no se puede» y un botón que da error.
 */
@Component({
  selector: 'app-reporteria',
  templateUrl: './reporteria.component.html',
  styleUrls: ['./reporteria.component.scss'],
})
export class ReporteriaComponent implements OnInit {
  private readonly api = inject(ApiReportsService)
  private readonly auth = inject(AuthService)
  private readonly toast = inject(MessageService)

  tarjetas: TarjetaReporte[] = []
  filas: ReportHistoryRow[] = []

  cargandoCatalogo = true
  cargandoHistorial = true
  /** El id de la fila que se está bajando, para poner el spinner solo en su botón. */
  bajando: string | null = null

  total = 0
  page = 0
  readonly limit = 15

  /** El filtro por tipo. `null` = todos. */
  tipo: ReportType | null = null
  readonly opcionesTipo = [{ label: 'Todos los reportes', value: null }, ...REPORT_CATALOG.map((r) => ({ label: r.label, value: r.type }))]

  ngOnInit(): void {
    this.cargarCatalogo()
    this.cargarHistorial()
  }

  // ── Catálogo ────────────────────────────────────────────────────────────────────────

  private cargarCatalogo(): void {
    this.cargandoCatalogo = true
    this.api.resumen().subscribe((resumen) => {
      const porTipo = new Map<string, ReportSummaryRow>(resumen.filter((r) => r.reportType).map((r) => [r.reportType as string, r]))
      this.tarjetas = REPORT_CATALOG.map((entrada) => {
        const pulso = porTipo.get(entrada.type)
        return {
          ...entrada,
          total: pulso?.total ?? 0,
          last: pulso?.last ?? null,
          lastBy: pulso?.lastBy ?? null,
          alcanzable: this.auth.userHasAuthorization(entrada.perm[0], entrada.perm[1]),
        }
      })
      this.cargandoCatalogo = false
    })
  }

  // ── Historial ───────────────────────────────────────────────────────────────────────

  //* El tipo lo pone PrimeNG y no una forma escrita a mano: en `TableLazyLoadEvent` tanto `first`
  //* como `rows` son opcionales **y anulables**, así que cualquier aproximación propia rompe la
  //* compilación de la plantilla en cuanto se afina.
  cargarHistorial(evento?: TableLazyLoadEvent): void {
    this.cargandoHistorial = true
    if (evento?.first != null && evento?.rows) this.page = Math.floor(evento.first / evento.rows)

    //* `status: success` a secas: una entrada fallida no tiene archivo que bajar y no es un reporte
    //* que alguien se haya llevado. Se filtra aquí y no en el backend porque el historial también lo
    //* consume la pantalla de Cortes, que sí quiere ver los intentos fallidos del corte automático.
    const filtros: Record<string, any> = { status: { value: 'success', matchMode: 'equals' } }
    if (this.tipo) filtros['reportType'] = { value: this.tipo, matchMode: 'equals' }

    this.api.historial(this.page + 1, this.limit, filtros).subscribe((pagina) => {
      this.cargandoHistorial = false
      this.filas = pagina?.data ?? []
      this.total = pagina?.totalItems ?? 0
    })
  }

  filtrarPorTipo(): void {
    this.page = 0
    this.cargarHistorial()
  }

  descargar(fila: ReportHistoryRow): void {
    this.bajando = fila._id
    this.api.descargar(fila._id).subscribe((blob) => {
      this.bajando = null
      if (!blob) return this.toast.add({ severity: 'error', summary: 'Reportería', detail: 'No se pudo recuperar ese archivo', life: 5000 })
      guardarBlobComo(blob, fila.fileName || 'reporte.xlsx')
    })
  }

  // ── Presentación ────────────────────────────────────────────────────────────────────

  etiqueta(tipo: ReportType | null): string {
    //* Las entradas anteriores a F-098 no traen tipo, y todas eran el corte de cartera.
    if (!tipo) return 'Corte de cartera'
    return REPORT_CATALOG_BY_TYPE.get(tipo)?.label ?? tipo
  }

  icono(tipo: ReportType | null): string {
    if (!tipo) return 'fa-solid fa-file-csv'
    return REPORT_CATALOG_BY_TYPE.get(tipo)?.icon ?? 'fa-solid fa-file-lines'
  }

  quien(fila: ReportHistoryRow): string {
    const u = fila.executedBy
    if (!u) return 'Automático'
    if (typeof u === 'string') return 'Usuario'
    return [u.names, u.lastNames].filter(Boolean).join(' ').trim() || u.email || 'Usuario'
  }

  /**
   * Con qué se pidió el reporte, en una línea legible.
   *
   * Es la columna que convierte el historial en algo accionable: dos cortes de cartera del mismo día
   * se distinguen por su mes y por si estaba cerrado, no por la hora.
   */
  conQue(fila: ReportHistoryRow): string {
    const p = fila.params || {}
    const partes: string[] = []
    if (p['month']) partes.push(this.mesLargo(p['month']))
    if (p['asOf']) partes.push(`al ${this.fechaCorta(p['asOf'])}`)
    if (p['closed'] === true) partes.push('mes cerrado')
    if (p['closed'] === false) partes.push('mes abierto')
    if (p['moraSwitchEnabled'] === false) partes.push('simulada')
    if (p['bank']) partes.push(p['bank'] === 'COVALTO' ? 'Covalto' : p['bank'])
    if (typeof p['paraRegistrar'] === 'number') partes.push(`${p['paraRegistrar']} para registrar`)
    if (!partes.length && fila.cutoffDate) partes.push(`al ${this.fechaCorta(fila.cutoffDate)}`)
    return partes.join(' · ') || '—'
  }

  /** `2026-09` → «septiembre 2026». Sin `new Date`, que a medianoche UTC devuelve el mes anterior. */
  private mesLargo(month: string): string {
    const [y, m] = String(month).split('-').map(Number)
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
    return meses[m - 1] ? `${meses[m - 1]} ${y}` : String(month)
  }

  private fechaCorta(iso: string): string {
    const d = new Date(iso)
    return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
  }

  /** El peso del archivo. En KB hasta el mega, que es donde se queda cualquiera de estos. */
  peso(bytes: number): string {
    if (!bytes) return '—'
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  duracion(ms: number): string {
    if (!ms) return '—'
    return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`
  }
}
